// Unpublished Work © 2023-2024 Deere & Company.

import { Box, Button, SvgIcon, SvgIconProps, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { RBACRole } from "../../api/rbac/types";
import { ConfigContext } from "../../context/ConfigContext";
import { DealerAccountContext } from "../../context/DealerAccountContext";
import { UserContext } from "../../context/UserContext/UserContext";
import { ALL_ACCOUNTS } from "../../utils/constants";
import { getHomeUrl } from "../../utils/legacyHome";

export interface ErrorPageProps {
  title: string;
  subtitles?: string[];
  icon?: { Component: typeof SvgIcon; color: SvgIconProps["color"] };
  showBackToHomeLink?: boolean;
  showReloadBtn?: boolean;
  reloadFunction?: () => void;
  showLoginBtn?: boolean;
  showReturnHomeButton?: boolean;
  fullPage?: boolean;
}

const ErrorPage = (props: ErrorPageProps) => {
  const {
    title,
    subtitles = [],
    icon,
    showBackToHomeLink,
    showReloadBtn,
    reloadFunction,
    showLoginBtn,
    showReturnHomeButton,
    fullPage,
  } = props;
  const theme = useTheme();
  const { t } = useTranslation("common");
  const { JDPO_JAVA_URL, JDPOINT_URL, ENABLE_REMOVE_DASHBOARD_TAB } =
    useContext(ConfigContext);

  const user = useContext(UserContext);
  const { dealerAccount, selectedAccounts } = useContext(DealerAccountContext);

  const dealerAccountId =
    user?.is(RBACRole.CORPORATE_PARTS_MANAGER) && selectedAccounts.length > 1
      ? ALL_ACCOUNTS
      : (dealerAccount?.id as string);

  const homeUrl = getHomeUrl(
    JDPO_JAVA_URL,
    JDPOINT_URL,
    dealerAccountId ?? "",
    ENABLE_REMOVE_DASHBOARD_TAB
  );

  const pageStyles = fullPage
    ? {
        display: "flex",
        justifyContent: "center",
        position: "absolute",
        top: "0px",
        bottom: "0px",
        left: "0px",
        right: "0px",
      }
    : {
        display: "flex",
        justifyContent: "center",
      };

  return (
    <Box sx={pageStyles} data-testid="error-page">
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {icon && (
          <icon.Component
            data-testid="error-icon"
            color={icon.color}
            fontSize="large"
          />
        )}
        <Typography
          sx={{
            textAlign: "center",
            paddingTop: theme.tokens.FuelSpacing6,
            paddingBottom: theme.tokens.FuelSpacing3,
            fontWeight: theme.tokens.FuelFontWeightSemibold,
          }}
          data-testid="error-title"
          component="h2"
        >
          {title}
        </Typography>
        <Box data-testid="error-subtitles">
          {subtitles.map((content, idx) => (
            <Typography
              sx={{ textAlign: "center" }}
              key={`subtitle-${idx + 1}`}
            >
              {content}
            </Typography>
          ))}
        </Box>
        {showReloadBtn && (
          <Button
            sx={{ marginTop: theme.tokens.FuelSpacing6, width: "140px" }}
            variant="primary"
            data-testid="error-reloadScreen"
            onClick={reloadFunction}
          >
            {t("authentication.failed.reload")}
          </Button>
        )}
        {showReturnHomeButton && (
          <Link
            data-testid="return-home-button"
            style={{
              textDecoration: "none",
              marginTop: theme.tokens.FuelSpacing6,
              color: theme.tokens.FuelColorTextPrimary,
            }}
            to={homeUrl}
          >
            <Button
              sx={{ width: "fit-content", minWidth: "140px" }}
              variant="primary"
            >
              {t("authentication.failed.returnHome")}
            </Button>
          </Link>
        )}
        {showBackToHomeLink && (
          <Link
            to={homeUrl}
            style={{
              marginTop: theme.tokens.FuelSpacing6,
              color: theme.tokens.FuelColorBrandPrimary,
              fontWeight: theme.tokens.FuelFontWeightSemibold,
              textDecoration: "none",
            }}
            data-testid="error-backToHomeButton"
          >
            {t("authentication.failed.returnHome")}
          </Link>
        )}
        {showLoginBtn && (
          <Link
            data-testid="login-button"
            style={{
              textDecoration: "none",
              marginTop: theme.tokens.FuelSpacing6,
              color: theme.tokens.FuelColorTextPrimary,
            }}
            to={homeUrl}
          >
            <Button sx={{ width: "140px" }} variant="primary">
              {t("authentication.failed.login")}
            </Button>
          </Link>
        )}
      </Box>
    </Box>
  );
};

export default ErrorPage;
